import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Button } from '@fiverr-private/button';
import { getContext } from '@fiverr-private/fiverr_context';
import { BriefIcon } from '@fiverr-private/icons';
import { BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { BriefDrawer } from '@fiverr-private/ai_brief';
import { useBusinessSignInSteps } from '@fiverr-private/identification_triggers';
import { I18n } from '@fiverr-private/i18n-react';
import { black } from '@fiverr-private/sass/helpers';
import { SafeWrapper } from '@fiverr-private/seller_pages_toolbox';
import { useFreelancerPageContext } from '../../../FreelancerPageContext';
import { PAGE_NAME, useMonitoring } from '../../../../monitoring';
import { useRelevantGigs } from '../../../../hooks/useRelevantGigs';
import { useBriefAiAvailablity } from '../useBriefAiAvailability';
import styles from '../ContactButton/index.module.scss';

const T_PREFIX = 'freelancer_page.ai_brief';

const COMPONENT_NAME = 'ai_brief_button';

/** @typedef {React.ComponentProps<typeof import('@fiverr-private/button')['Button']>} ButtonProps */
/** @type {React.FC<{className?: string; source: string; size?: ButtonProps['size']; }>} */
const AiBriefButtonView = ({ className, source, size }) => {
    const [isShowDrawer, setIsShowDrawer] = useState(false);
    const [isDrawerLoading, setIsDrawerLoading] = useState(false);
    const showButton = useBriefAiAvailablity();
    const { sellerId, seller, gigs } = useFreelancerPageContext();
    const sellerUsername = seller?.user?.name;
    const sellerDisplayName = seller?.user?.profile?.displayName || sellerUsername;
    const responseTime = seller?.responseTime?.inHours;
    const { Providers, fireImpressionEvents } = useMonitoring();

    const mostRelevantGig = useRelevantGigs(gigs)?.[0];

    const { userId, organizationId } = getContext();

    const sendBiImpressionEvent = useCallback(() => {
        fireImpressionEvents({
            [Providers.BigQuery]: {
                type: 'professional_brief_button_shown',
                group: 'user_impression',
                sub_group: 'fiverr_pro',
                user: {
                    id: userId,
                },
                seller: {
                    id: Number(sellerId),
                },
                organization: {
                    id: organizationId,
                },
                action: {
                    type: 'imp',
                },
                page: {
                    name: PAGE_NAME,
                    element: {
                        name: 'button',
                    },
                },
                feature: {
                    name: 'fiverr_pro',
                },
                gig: {
                    id: mostRelevantGig?.gig_id,
                },
                buyer: {
                    request: {
                        category_id: mostRelevantGig?.category_id,
                        subcategory_id: mostRelevantGig?.sub_category_id,
                    },
                },
                out_of_session: 'False',
            },
        });
    }, [Providers.BigQuery, fireImpressionEvents, mostRelevantGig, organizationId, sellerId, userId]);

    useEffect(() => {
        if (showButton) {
            sendBiImpressionEvent();
        }
    }, [showButton, sendBiImpressionEvent]);

    const openDrawer = () => {
        setIsDrawerLoading(true);
        setIsShowDrawer(true);
    };

    const { signInSteps } = useBusinessSignInSteps({
        id: `brief-drawer-${sellerId}`,
        onSuccess: openDrawer,
        signInModalParams: {
            source: PAGE_NAME,
            businessTriggerCtaType: BUSINESS_TRIGGER_CTA.NO_RESULT_PAGE_BRIEF,
        },
        migrateModalParams: {
            source: PAGE_NAME,
            biEnrichment: {
                pageElementName: source,
                trigger: BUSINESS_TRIGGER_CTA.NO_RESULT_PAGE_BRIEF,
            },
        },
    });

    if (!showButton) {
        return null;
    }

    const closeDrawer = () => {
        setIsShowDrawer(false);
        setIsDrawerLoading(false);
    };

    return (
        <div className={className}>
            <Button
                size={size}
                className={classnames(styles.contactButton, 'flex-center')}
                onClick={signInSteps}
                intent="primary"
                variant="outline"
                loading={isDrawerLoading}
            >
                <span className="flex flex-center">
                    {!isDrawerLoading && <BriefIcon color={black} size={24} />}
                    <span className="p-l-8">
                        <I18n k={`${T_PREFIX}.button`} />
                    </span>
                </span>
            </Button>
            <BriefDrawer
                componentName={COMPONENT_NAME}
                source={PAGE_NAME}
                seller={{
                    id: Number(sellerId),
                    userName: sellerUsername,
                    displayName: sellerDisplayName,
                    responseTime,
                }}
                shouldRefreshData={isShowDrawer}
                categoryId={mostRelevantGig?.category_id}
                subCategoryId={mostRelevantGig?.sub_category_id}
                isOpen={isShowDrawer && !isDrawerLoading}
                onClose={closeDrawer}
                onLoadingChange={setIsDrawerLoading}
            />
        </div>
    );
};

/** @type {typeof AiBriefButtonView} */
export const AiBriefButton = SafeWrapper(AiBriefButtonView, 'AiBriefDrawerView');
